import { createPortal } from "react-dom";
import { Exercise } from "../../types/conversations";
import utils from "../../helpers/utils";

interface ExerciseViewModalProps {
  isOpen: boolean;
  exercise: Exercise | null;
  onClose: () => void;
}

export const ExerciseViewModal = ({
  isOpen,
  exercise,
  onClose,
}: ExerciseViewModalProps) => {
  if (!isOpen || !exercise) return null;

  const imageUrl =
    exercise.image_url ||
    exercise.image ||
    utils.getUriImage(exercise.image ?? "", "all_view");

  return createPortal(
    <div
      className="fixed inset-0 z-[9999999] flex items-center justify-center bg-black/50 backdrop-blur-sm overflow-y-auto transition-all duration-300 p-2 sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="relative w-full max-w-full sm:max-w-[600px] bg-white shadow-sm dark:bg-boxdark dark:border dark:border-strokedark !rounded-xl sm:!rounded-2xl p-4 sm:p-6 overflow-hidden">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="absolute right-2 top-2 sm:right-4 sm:top-4 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          aria-label="Fechar modal"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <div className="mb-4 pr-8 sm:pr-0">
          <h3 className="text-lg sm:text-xl font-bold text-gray-800 dark:text-white">
            {exercise.name}
          </h3>
        </div>
        <div className="overflow-y-auto custom-scrollbar max-h-[85vh]">
          <div className="space-y-4">
            {/* Imagem do exercício */}
            {imageUrl && (
              <div className="w-full h-64 sm:h-80 rounded-lg overflow-hidden">
                <img
                  src={imageUrl}
                  alt={exercise.name}
                  className="w-full object-contain"
                />
              </div>
            )}

            {/* Descrição */}
            {exercise.description && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Descrição
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                  {exercise.description}
                </p>
              </div>
            )}

            {/* Informações adicionais */}
            {(exercise.video || exercise.professional_id) && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Informações
                </h4>
                <div className="space-y-2">
                  {exercise.video && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Vídeo:</span>{" "}
                      <a
                        href={exercise.video}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-600 dark:text-purple-400 hover:underline"
                      >
                        Assistir vídeo
                      </a>
                    </div>
                  )}
                  {exercise.all_view !== undefined && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Visibilidade:</span>{" "}
                      {exercise.all_view ? "Público" : "Privado"}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
